import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import api from 'utils/api';
import { useAuth } from './AuthContext'; // Asumo que tu AuthContext está en la misma carpeta

// 1. Crear el Contexto
const BusinessContext = createContext();

// 2. Crear el Proveedor del Contexto (El componente que envolverá tu app)
export const BusinessProvider = ({ children }) => {
    const { user } = useAuth(); // Obtenemos el usuario del AuthContext

    const [businessList, setBusinessList] = useState([]);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to load businesses
    const loadBusinesses = React.useCallback(async () => {
        if (!user?._id) {
            setLoading(false);
            return;
        }

        // Check if admin is impersonating
        const isAdminImpersonating = localStorage.getItem("isAdminImpersonating") === "true";
        const impersonatingBusinessStr = localStorage.getItem("adminImpersonatingBusiness");

        if (isAdminImpersonating && impersonatingBusinessStr) {
            // If admin is impersonating, use the saved business
            try {
                const impersonatingBusiness = JSON.parse(impersonatingBusinessStr);
                setBusinessList([impersonatingBusiness]);
                setSelectedBusiness(impersonatingBusiness);
                setLoading(false);
                return;
            } catch (err) {
                console.error("Error parsing impersonating business:", err);
                // If error, continue with normal flow
            }
        }

        setLoading(true);
        setError(null);

        try {
            const res = await api.get(`/business/by_user/${user._id}`);
            const businesses = res.data.content || [];
            setBusinessList(businesses);
            // console.log("Fetched businesses:", businesses);
            // If there are businesses, select the first one by default
            if (businesses.length > 0) {
                // Look for a saved business in localStorage to maintain selection
                const lastSelectedId = localStorage.getItem('selectedBusinessId');
                const lastSelected = businesses.find(b => b._id === lastSelectedId);
                setSelectedBusiness(lastSelected || businesses[0]);
            } else {
                setSelectedBusiness(null);
            }

        } catch (err) {
            console.error("Could not load businesses:", err);
            setError('Could not load businesses.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    // useEffect para obtener los negocios cuando el usuario cambia (ej. al iniciar sesión)
    useEffect(() => {
        loadBusinesses();
    }, [loadBusinesses]);

    // Listen for changes in impersonation business (for admin switching businesses)
    useEffect(() => {
        const handleStorageChange = () => {
            const isAdminImpersonating = localStorage.getItem("isAdminImpersonating") === "true";
            const impersonatingBusinessStr = localStorage.getItem("adminImpersonatingBusiness");
            
            if (isAdminImpersonating && impersonatingBusinessStr) {
                try {
                    const impersonatingBusiness = JSON.parse(impersonatingBusinessStr);
                    setBusinessList([impersonatingBusiness]);
                    setSelectedBusiness(impersonatingBusiness);
                } catch (err) {
                    console.error("Error parsing impersonating business:", err);
                }
            }
        };

        // Listen for storage events (when localStorage changes in another tab/window)
        window.addEventListener('storage', handleStorageChange);
        
        // Also listen for custom events (when localStorage changes in same tab)
        window.addEventListener('businessImpersonationChanged', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('businessImpersonationChanged', handleStorageChange);
        };
    }, []);

    // Función para actualizar el negocio seleccionado
    const handleSelectBusiness = (business) => {
        setSelectedBusiness(business);
        // Guardamos en localStorage para persistir la selección entre recargas
        if (business?._id) {
            localStorage.setItem('selectedBusinessId', business._id);
        } else {
            localStorage.removeItem('selectedBusinessId');
        }
    };

    // Usamos useMemo para evitar que el objeto 'value' se recree en cada render
    const value = useMemo(() => ({
        businessList,
        selectedBusiness,
        handleSelectBusiness,
        loading,
        error
    }), [businessList, selectedBusiness, loading, error]);

    return (
        <BusinessContext.Provider value={value}>
            {children}
        </BusinessContext.Provider>
    );
};

// 3. Crear un Hook personalizado para usar el contexto fácilmente
export const useBusiness = () => {
    const context = useContext(BusinessContext);
    if (context === undefined) {
        throw new Error('useBusiness must be used within a BusinessProvider');
    }
    return context;
};